/**
 * NarrationAudioControls - Audio controls panel for narration view
 *
 * Shows:
 * - Audio generation status/overlay
 * - Audio playback with word-wise highlighting
 * - Version management
 * - Apply/sync animations
 *
 * This component is used below SlideStage in the NarrationView.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AudioReviewCard } from '@/components/slides/narration/AudioReviewCard';
import { GeneratingOverlay } from '@/components/slides/narration/GeneratingOverlay';
import {
  getSlideAudio,
  listAudioVersions,
  switchAudioVersion,
  applyAudioWithChoreography,
  type SlideAudioVersion,
  type AppliedVersionInfo,
  type WordTiming,
} from '@/services/audioService';
import { cancelAgentJob, executeAgentJob } from '@/services/agentJobService';
import { useApplyJobStatus } from '@/hooks/useApplyJobStatus';
import { useNarrationAudioSrc } from '@/hooks/useNarrationAudioSrc';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const SHOW_NARRATION_ADVANCED = import.meta.env.VITE_SHOW_NARRATION_ADVANCED === 'true';

export type AudioStatus = 'none' | 'pending' | 'generating' | 'aligning' | 'completed' | 'failed';

export interface MasterAudioConfig {
  audioUrl: string;
  audioRange: { startTime: number; endTime: number };
  wordTimings: WordTiming[];
  scriptText: string;
  slideIdentifier: string;
}

interface NarrationAudioControlsProps {
  slideId: string;
  slideIdentifier: string;
  conversationId: string;
  scriptText: string | null;
  audioStatus: AudioStatus;
  isPlaying: boolean;
  onPlayChange: (playing: boolean) => void;
  onGenerateAudio: () => void;
  onAudioApplied?: () => void;
  className?: string;
  masterAudioConfig?: MasterAudioConfig;
}

export const NarrationAudioControls: React.FC<NarrationAudioControlsProps> = ({
  slideId,
  slideIdentifier,
  conversationId,
  scriptText,
  audioStatus,
  isPlaying,
  onPlayChange,
  onGenerateAudio,
  onAudioApplied,
  className,
  masterAudioConfig,
}) => {
  // Detailed audio data
  const [currentVersion, setCurrentVersion] = useState<SlideAudioVersion | null>(null);
  const [versions, setVersions] = useState<SlideAudioVersion[]>([]);
  const [appliedVersion, setAppliedVersion] = useState<AppliedVersionInfo | null>(null);
  const [wordTimings, setWordTimings] = useState<WordTiming[] | null>(null);

  const [audioLoadError, setAudioLoadError] = useState<string | null>(null);

  // Playback state
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Apply/Sync state - now managed by useApplyJobStatus hook for persistence
  const {
    applyingSlideIds,
    slideApplyProgress,
    registerJob: registerApplyJob,
  } = useApplyJobStatus(conversationId);

  // Derive apply state from hook (replaces local state)
  const isApplying = applyingSlideIds.has(slideId);
  const applyProgress = slideApplyProgress[slideId] || null;
  const [applyError, setApplyError] = useState<string | null>(null);

  // Track choreography job ID for cancellation (hook manages SSE subscription)
  const [choreographyJobId, setChoreographyJobId] = useState<string | null>(null);

  // Generation progress tracking
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStatus, setGenerationStatus] = useState<'idle' | 'GENERATING' | 'ALIGNING' | 'COMPLETED' | 'FAILED'>('idle');
  const [isLocalGenerating, setIsLocalGenerating] = useState(false);

  const hasScript = !!scriptText;

  // Use local generating state OR parent audioStatus
  const isGenerating = masterAudioConfig ? false : (isLocalGenerating || audioStatus === 'generating' || audioStatus === 'aligning');
  const isCompleted = masterAudioConfig ? true : (!isGenerating && (audioStatus === 'completed' || currentVersion?.status === 'COMPLETED'));

  const narration = useNarrationAudioSrc(
    !masterAudioConfig && isCompleted && currentVersion ? slideId : null,
    currentVersion?.version,
    currentVersion?.audioUrl,
  );
  const { onError: onNarrationError } = narration;
  // Master audio plays its own signed URL
  const audioSrc = masterAudioConfig ? masterAudioConfig.audioUrl : narration.src;

  // Load detailed audio data when slideId changes (skip in master audio mode)
  useEffect(() => {
    if (!slideId || masterAudioConfig) return;

    let cancelled = false;

    const loadAudioDetails = async () => {
      setAudioLoadError(null);

      try {
        const [audioData, versionsData] = await Promise.all([
          getSlideAudio(slideId).catch(() => null),
          listAudioVersions(slideId).catch(() => []),
        ]);

        if (cancelled) return;

        if (audioData) {
          setCurrentVersion(audioData);
          setAppliedVersion(audioData.appliedVersion);
          setWordTimings(audioData.wordTimings);
        } else {
          setCurrentVersion(null);
          setAppliedVersion(null);
          setWordTimings(null);
        }

        setVersions(versionsData);
      } catch (err) {
        console.error('[NarrationAudioControls] Failed to load audio details:', err);
      }
    };

    loadAudioDetails();

    return () => {
      cancelled = true;
    };
  }, [slideId, masterAudioConfig]);

  // Track previous slideId to detect slide changes
  const prevSlideIdRef = useRef<string | null>(null);

  // Reset state when slideId changes
  useEffect(() => {
    if (prevSlideIdRef.current === slideId) return;
    prevSlideIdRef.current = slideId;

    // Reset playback state
    setCurrentTime(0);
    setDuration(0);
    setCurrentWordIndex(-1);

    // Reset apply state (error only - apply state is managed by hook)
    setApplyError(null);
    setChoreographyJobId(null);

    // Reset generation tracking
    setIsLocalGenerating(false);
    setGenerationStatus('idle');
    setGenerationProgress(0);
  }, [slideId]);

  // Master audio mode: set up audio source, word timings, and synthetic version
  useEffect(() => {
    if (!masterAudioConfig) return;

    const { audioUrl, audioRange, wordTimings: masterTimings, scriptText: masterScript } = masterAudioConfig;
    const slideDuration = audioRange.endTime - audioRange.startTime;

    setWordTimings(masterTimings);
    setDuration(slideDuration);

    // Create synthetic version so AudioReviewCard renders
    const syntheticVersion: SlideAudioVersion = {
      id: 'master-audio',
      slideAudioId: 'master-audio',
      version: 1,
      scriptText: masterScript,
      scriptSource: 'user',
      voiceId: 'master',
      voiceName: 'Master Audio',
      provider: 'master',
      languageCode: 'en-US',
      ttsModel: null,
      audioUrl: audioUrl,
      audioDuration: slideDuration,
      wordTimings: masterTimings,
      status: 'COMPLETED',
      generationJobId: null,
      errorMessage: null,
      isCurrentVersion: true,
      createdBy: 'system',
      createdAt: new Date().toISOString(),
    };

    setCurrentVersion(syntheticVersion);
    setVersions([syntheticVersion]);
    setAppliedVersion(null);
    setAudioLoadError(null);
  }, [masterAudioConfig?.audioUrl, masterAudioConfig?.audioRange.startTime, masterAudioConfig?.audioRange.endTime]);

  // Sync local generating state with parent audioStatus
  useEffect(() => {
    if (audioStatus === 'generating' || audioStatus === 'aligning') {
      if (!isLocalGenerating) {
        setIsLocalGenerating(true);
        setGenerationStatus(audioStatus === 'aligning' ? 'ALIGNING' : 'GENERATING');
        if (generationProgress === 0) {
          setGenerationProgress(audioStatus === 'aligning' ? 50 : 10);
        }
      }
    }
  }, [audioStatus, isLocalGenerating, generationProgress]);

  // Polling fallback for status updates when generating (skip in master audio mode)
  useEffect(() => {
    if (masterAudioConfig) return;
    const shouldPoll = isLocalGenerating || audioStatus === 'generating' || audioStatus === 'aligning';

    if (shouldPoll && slideId) {
      const interval = setInterval(async () => {
        try {
          const audioData = await getSlideAudio(slideId);
          if (audioData) {
            if (audioData.status === 'COMPLETED') {
              setCurrentVersion(audioData);
              setAppliedVersion(audioData.appliedVersion);
              setWordTimings(audioData.wordTimings);
              setIsLocalGenerating(false);
              setGenerationStatus('COMPLETED');
              setGenerationProgress(100);

              const versionsData = await listAudioVersions(slideId);
              setVersions(versionsData);
            } else if (audioData.status === 'FAILED') {
              setIsLocalGenerating(false);
              setGenerationStatus('FAILED');
            }
          }
        } catch (err) {
          console.error('[NarrationAudioControls] Polling error:', err);
        }
      }, 2000);

      return () => clearInterval(interval);
    }
  }, [slideId, isLocalGenerating, audioStatus]);

  // Handle audio element playback control
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      // In master audio mode, seek to audioRange.startTime before playing
      if (masterAudioConfig) {
        const { startTime, endTime } = masterAudioConfig.audioRange;
        // Only seek if we're outside the range or at position 0
        if (audio.currentTime < startTime || audio.currentTime >= endTime) {
          audio.currentTime = startTime;
        }
      }
      audio.play().catch((err) => {
        console.error('[NarrationAudioControls] Playback failed:', err);
        onPlayChange(false);
      });
    } else {
      audio.pause();
    }
  }, [isPlaying, onPlayChange, masterAudioConfig]);

  // Listen for SSE audio events (skip in master audio mode)
  useEffect(() => {
    if (masterAudioConfig) return;
    const handleAudioEvent = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const audioEvent = customEvent.detail?.audioEvent || {};
      const {
        slideId: eventSlideId,
        slideIdentifier: eventSlideIdentifier,
        status,
        progress,
        type
      } = audioEvent;

      const isOurSlide = (eventSlideId && eventSlideId === slideId) ||
                         (eventSlideIdentifier && eventSlideIdentifier === slideIdentifier);

      if (!isOurSlide) return;

      // Handle progress updates
      if (type === 'audio_status') {
        if (progress !== undefined) {
          setGenerationProgress(progress);
        }

        if (status === 'GENERATING' || status === 'ALIGNING') {
          setIsLocalGenerating(true);
          setGenerationStatus(status);
        }

        if (status === 'COMPLETED') {
          setGenerationProgress(100);
          setGenerationStatus('COMPLETED');
          setIsLocalGenerating(false);

          try {
            const [audioData, versionsData] = await Promise.all([
              getSlideAudio(slideId),
              listAudioVersions(slideId),
            ]);

            if (audioData) {
              setCurrentVersion(audioData);
              setAppliedVersion(audioData.appliedVersion);
              setWordTimings(audioData.wordTimings);
            }
            setVersions(versionsData);
          } catch (err) {
            console.error('[NarrationAudioControls] Failed to refresh audio:', err);
          }
        }

        if (status === 'FAILED') {
          setGenerationProgress(0);
          setGenerationStatus('FAILED');
          setIsLocalGenerating(false);
        }
      }

      // Handle audio_generated event
      if (type === 'audio_generated') {
        setGenerationProgress(100);
        setGenerationStatus('COMPLETED');
        setIsLocalGenerating(false);

        try {
          const [audioData, versionsData] = await Promise.all([
            getSlideAudio(slideId),
            listAudioVersions(slideId),
          ]);

          if (audioData) {
            setCurrentVersion(audioData);
            setAppliedVersion(audioData.appliedVersion);
            setWordTimings(audioData.wordTimings);
          }
          setVersions(versionsData);
        } catch (err) {
          console.error('[NarrationAudioControls] Failed to refresh audio:', err);
        }
      }

      // Handle audio_error event
      if (type === 'audio_error') {
        setGenerationProgress(0);
        setGenerationStatus('FAILED');
        setIsLocalGenerating(false);
      }
    };

    window.addEventListener('audio-event', handleAudioEvent);
    return () => window.removeEventListener('audio-event', handleAudioEvent);
  }, [slideId, slideIdentifier]);

  // Note: SSE subscriptions are now managed by useApplyJobStatus hook

  // Listen for slides-updated events to refresh applied version after choreography completes
  useEffect(() => {
    const handleSlidesUpdated = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { conversationId: eventConvId, source } = customEvent.detail || {};

      // Only refresh if it's from animation choreographer and matches our conversation
      if (eventConvId === conversationId && source === 'animation_choreographer') {
        console.log('[NarrationAudioControls] Choreography completed, refreshing applied version');

        // Clear choreography job ID since it's complete
        setChoreographyJobId(null);

        // Refresh audio data to get updated applied version
        try {
          const audioData = await getSlideAudio(slideId);
          if (audioData) {
            setAppliedVersion(audioData.appliedVersion);
          }
        } catch (err) {
          console.error('[NarrationAudioControls] Failed to refresh applied version:', err);
        }

        // Notify parent
        onAudioApplied?.();
      }
    };

    window.addEventListener('slides-updated', handleSlidesUpdated);
    return () => window.removeEventListener('slides-updated', handleSlidesUpdated);
  }, [slideId, conversationId, onAudioApplied]);

  // Handle version switch (no-op in master audio mode — single version only)
  const handleVersionChange = useCallback(
    async (version: number) => {
      if (masterAudioConfig) return;
      try {
        const updated = await switchAudioVersion(slideId, version);
        // The switch answers with the stored row, whose audioUrl was signed at generation and has expired;
        // the versions list carries a freshly signed one.
        const versionsData = await listAudioVersions(slideId);
        const fresh = versionsData.find((v) => v.version === updated.version);
        setCurrentVersion({ ...updated, audioUrl: fresh?.audioUrl ?? null });
        setWordTimings(updated.wordTimings);
        setVersions(versionsData);

        toast.success(`Switched to version ${version}`);
      } catch (err) {
        console.error('[NarrationAudioControls] Failed to switch version:', err);
        toast.error('Failed to switch version');
      }
    },
    [slideId, masterAudioConfig]
  );

  // Handle apply audio to slide - uses shared function + hook for state management
  // In master audio mode: trigger choreography directly (no applyAudioToSlide step)
  const handleApply = useCallback(async () => {
    if (isApplying) return;

    setApplyError(null);

    // Master audio mode: direct choreography trigger
    if (masterAudioConfig) {
      const { wordTimings: masterTimings, audioRange, scriptText: masterScript, slideIdentifier: masterSlideId } = masterAudioConfig;

      if (!masterTimings || masterTimings.length === 0) {
        toast.info('No word timings available for choreography');
        return;
      }

      try {
        const slideDir = `slides/${masterSlideId}`;
        const audioDur = audioRange.endTime - audioRange.startTime;

        const response = await executeAgentJob({
          conversationId,
          agentName: 'animation_choreographer',
          inputs: {
            slideDirectory: slideDir,
            wordTimings: JSON.stringify(masterTimings),
            audioDuration: audioDur,
            scriptText: masterScript,
          },
          metadata: {
            slideId,
            slideIdentifier: masterSlideId,
          },
        });

        const jobId = response.job.id;
        setChoreographyJobId(jobId);
        registerApplyJob(slideId, jobId);

        toast.success('Syncing animations with audio...', { duration: 2000 });
      } catch (err: any) {
        console.error('[NarrationAudioControls] Failed to trigger choreography:', err);
        setApplyError(err.message || 'Failed to sync animations');
        toast.error('Failed to sync animations');
      }
      return;
    }

    // Standard mode: apply audio + choreography
    if (!currentVersion) return;

    try {
      const result = await applyAudioWithChoreography({
        slideId,
        slideIdentifier: slideIdentifier || slideId,
        conversationId,
        versionId: currentVersion.id,
        scriptText: scriptText || '',
        registerJob: (slideId, jobId) => {
          // Save job ID for cancellation tracking
          setChoreographyJobId(jobId);
          // Register with hook for SSE tracking and progress updates
          registerApplyJob(slideId, jobId);
        },
      });

      if (!result.success) {
        setApplyError(result.error || 'Failed to apply audio');
        toast.error(result.error || 'Failed to apply audio');
        return;
      }

      // Update applied version tracking
      setAppliedVersion({
        id: currentVersion.id,
        version: currentVersion.version,
        voiceName: currentVersion.voiceName,
        voiceId: currentVersion.voiceId,
        audioDuration: currentVersion.audioDuration,
        appliedAt: new Date().toISOString(),
      });

      if (result.choreographyJobCreated) {
        // Choreography started - hook will track progress and notify completion
        toast.success('Syncing animations with audio...', { duration: 2000 });
      } else {
        // No choreography needed - audio applied successfully
        toast.success('Audio applied to slide');
        onAudioApplied?.();

        // Dispatch event for slide preview refresh
        window.dispatchEvent(new CustomEvent('slides-updated', {
          detail: {
            conversationId,
            slideId,
            timestamp: new Date().toISOString(),
            source: 'audio_apply',
          }
        }));
      }
    } catch (err: any) {
      console.error('[NarrationAudioControls] Failed to apply audio:', err);
      setApplyError(err.message || 'Failed to apply audio');
      toast.error('Failed to apply audio');
    }
  }, [slideId, slideIdentifier, currentVersion, conversationId, scriptText, onAudioApplied, isApplying, registerApplyJob, masterAudioConfig]);

  // Handle cancel apply/sync
  const handleCancelApply = useCallback(async () => {
    if (choreographyJobId) {
      try {
        await cancelAgentJob(choreographyJobId);
        // Note: The hook will handle state cleanup via SSE completion/error events
      } catch (err) {
        console.error('[NarrationAudioControls] Failed to cancel job:', err);
      }
    }
    setChoreographyJobId(null);
  }, [choreographyJobId]);

  // Listen for "Apply All" / "Sync All" trigger event from NarrationView
  // This allows the Apply All / Sync All Animations button to programmatically trigger each slide's Apply flow
  useEffect(() => {
    const handleTriggerApply = (event: CustomEvent<{ slideId: string; slideIdentifier?: string }>) => {
      const matches = event.detail.slideId === slideId ||
        (masterAudioConfig && event.detail.slideIdentifier === masterAudioConfig.slideIdentifier);
      if (matches && !isApplying && isCompleted) {
        console.log(`[NarrationAudioControls] Received trigger-apply-audio for ${slideIdentifier}`);
        handleApply();
      }
    };

    window.addEventListener('trigger-apply-audio', handleTriggerApply as EventListener);
    return () => {
      window.removeEventListener('trigger-apply-audio', handleTriggerApply as EventListener);
    };
  }, [slideId, slideIdentifier, isApplying, isCompleted, handleApply, masterAudioConfig]);

  // Audio event handlers
  const handlePlayPause = useCallback(() => {
    onPlayChange(!isPlaying);
  }, [isPlaying, onPlayChange]);

  const handleSeek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (audio) {
      if (masterAudioConfig) {
        // Offset seek by audioRange start time
        audio.currentTime = time + masterAudioConfig.audioRange.startTime;
      } else {
        audio.currentTime = time;
      }
      setCurrentTime(time);
    }
  }, [masterAudioConfig]);

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    let time: number;
    if (masterAudioConfig) {
      const { startTime, endTime } = masterAudioConfig.audioRange;
      // Compute visual time relative to slide start
      time = audio.currentTime - startTime;

      // If past the end of this slide's range, pause and trigger ended
      if (audio.currentTime >= endTime) {
        audio.pause();
        onPlayChange(false);
        setCurrentWordIndex(-1);
        setCurrentTime(endTime - startTime);
        window.dispatchEvent(new CustomEvent('audio-ended'));
        return;
      }
    } else {
      time = audio.currentTime;
    }

    setCurrentTime(time);

    if (wordTimings && wordTimings.length > 0) {
      const idx = wordTimings.findIndex(
        (wt) => time >= wt.startTime && time < wt.endTime
      );
      setCurrentWordIndex(idx);
    }
  }, [wordTimings, masterAudioConfig, onPlayChange]);

  const handleEnded = useCallback(() => {
    onPlayChange(false);
    setCurrentWordIndex(-1);
    window.dispatchEvent(new CustomEvent('audio-ended'));
  }, [onPlayChange]);

  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      if (masterAudioConfig) {
        // Override with slide-specific duration
        setDuration(masterAudioConfig.audioRange.endTime - masterAudioConfig.audioRange.startTime);
      } else {
        setDuration(audio.duration);
      }
    }
  }, [masterAudioConfig]);

  const handleCanPlayThrough = useCallback(() => {}, []);

  const handleAudioError = useCallback(() => {
    onPlayChange(false);
    if (masterAudioConfig) setAudioLoadError('Audio playback error');
    else onNarrationError();
  }, [onPlayChange, masterAudioConfig, onNarrationError]);

  const handleWordClick = useCallback((time: number) => {
    handleSeek(time);
  }, [handleSeek]);

  const handleRestart = useCallback(() => {
    handleSeek(0);
  }, [handleSeek]);

  return (
    <div className={cn('flex flex-col', className)}>
      <ScrollArea className="max-h-[300px]">
        <div className="p-4 space-y-4">
          {/* Generating Overlay */}
          {isGenerating && (
            <GeneratingOverlay
              progress={generationProgress || (generationStatus === 'ALIGNING' ? 50 : 10)}
              status={generationStatus === 'ALIGNING' ? 'ALIGNING' : 'GENERATING'}
              script={scriptText || undefined}
            />
          )}

          {/* Audio Review Card - when audio exists */}
          {isCompleted && currentVersion && (
            <AudioReviewCard
              versions={versions}
              currentVersion={currentVersion}
              appliedVersion={appliedVersion}
              onVersionChange={handleVersionChange}
              audioSrc={audioSrc}
              audioRef={audioRef as React.RefObject<HTMLAudioElement>}
              isPlaying={isPlaying}
              currentTime={currentTime}
              duration={duration || currentVersion.audioDuration || 0}
              onPlayPause={handlePlayPause}
              onSeek={handleSeek}
              onRestart={handleRestart}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleEnded}
              onLoadedMetadata={handleLoadedMetadata}
              onCanPlayThrough={handleCanPlayThrough}
              onAudioError={handleAudioError}
              wordTimings={wordTimings}
              currentWordIndex={currentWordIndex}
              onWordClick={handleWordClick}
              script={scriptText || ''}
              onApply={SHOW_NARRATION_ADVANCED ? handleApply : undefined}
              isApplying={SHOW_NARRATION_ADVANCED ? isApplying : false}
              applyProgress={SHOW_NARRATION_ADVANCED && applyProgress ? {
                percentage: applyProgress.percentage ?? 0,
                currentOperation: applyProgress.currentOperation ?? 'Processing...',
                thinking: applyProgress.thinking,
              } : null}
              onCancelApply={SHOW_NARRATION_ADVANCED && choreographyJobId ? handleCancelApply : undefined}
              applyError={SHOW_NARRATION_ADVANCED ? applyError : null}
              audioLoadError={audioLoadError ?? (narration.error && 'Audio playback error')}
              isLoadingAudio={narration.loading}
            />
          )}

          {/* Generate Audio Button - when no audio yet (hidden in master audio mode) */}
          {!masterAudioConfig && !isCompleted && !isGenerating && hasScript && slideId && (
            <Button
              onClick={onGenerateAudio}
              className="w-full h-10"
              disabled={!hasScript}
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generate Audio
            </Button>
          )}

          {/* No script message (hidden in master audio mode) */}
          {!masterAudioConfig && !hasScript && !isCompleted && !isGenerating && (
            <p className="text-sm text-gray-400 dark:text-zinc-500 italic text-center py-2">
              No script yet. Generate scripts to add narration.
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default NarrationAudioControls;
